import { useCallback, useEffect, useRef, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { DiarioObrasLista } from "../../domain/models/diario_obras_lista";
import { useDiarioObrasPagination } from "../hooks/diario_obras/pagination";
import { useDiarioObrasFilters } from "../hooks/diario_obras/filter";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useCreateDebounceHandler } from "../../../../common/hooks/use_debounced_callback";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { useForm } from "react-hook-form";
import {
  DiarioObrasForm,
  DiarioObrasSchema,
} from "../schemas/DiarioObras/diario_obras";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DiarioObrasEfetivoForm,
  DiarioObrasEfetivoSchema,
} from "../schemas/DiarioObrasEfetivo/diario_obras_efetivo";
import { DiarioObrasEfetivoLista } from "../../domain/models/diario_obras_efetivo_lista";
import { useDiarioObrasEfetivoFilters } from "../hooks/diario_obras_efetivo/filter";
import { useDiarioObrasEfetivoPagination } from "../hooks/diario_obras_efetivo/pagination";
import { useDiarioObrasEquipamentoPagination } from "../hooks/diario_obras_equipamento/pagination";
import { useDiarioObrasEquipamentoFilters } from "../hooks/diario_obras_equipamento/filter";
import { DiarioObrasEquipamentoLista } from "../../domain/models/diario_obras_equipamento_lista";
import {
  DiarioObrasEquipamentoForm,
  DiarioObrasEquipamentoSchema,
} from "../schemas/DiarioObrasEquipamento/diario_obras_equipamento";
import { useDiarioObrasAtividadesPagination } from "../hooks/diario_obras_atividades/pagination";
import { useDiarioObrasAtividadesFilters } from "../hooks/diario_obras_atividades/filter";
import {
  DiarioObrasAtividadesForm,
  DiarioObrasAtividadesSchema,
} from "../schemas/DiarioObrasAtividades/diario_obras_atividades";
import { DiarioObrasAtividadesLista } from "../../domain/models/diario_obras_atividades_lista";
import {
  DiarioObrasParalisacoesForm,
  DiarioObrasParalisacoesSchema,
} from "../schemas/DiarioObrasParalisacoes/diario_obras_paralisacoes";
import { DiarioObrasParalisacoesLista } from "../../domain/models/diario_obras_paralisacoes_lista";
import { useDiarioObrasParalisacoesFilters } from "../hooks/diario_obras_paralisacoes/filter";
import { useDiarioObrasParalisacoesPagination } from "../hooks/diario_obras_paralisacoes/pagination";
import { useDiarioObrasImagensPagination } from "../hooks/diario_obras_imagens/pagination";
import { useDiarioObrasImagensFilters } from "../hooks/diario_obras_imagens/filter";
import {
  DiarioObrasImagensForm,
  DiarioObrasImagensSchema,
} from "../schemas/DiarioObrasImagens/diario_obras_imagens";
import { DiarioObrasImagem } from "../../domain/models/diario_obras_imagens_lista";
import { FileUtils } from "../../../../common/utils/file_utils";
import { useNavigate, useParams } from "react-router";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { FuncionarioTercDropDown } from "../../../../common/domain/models/funcionario_terc_dropdown";
import { useUserAuthStore } from "../../../../stores/global/userAuth";

type LoadDiarioObraFuncState = {
  IdDiarioObras?: number;
  IdDiarioObrasEfetivo?: number;
  page?: number;
  rowsPerPage?: number;
};
type LoadDiarioObraEquipamentoFuncState = {
  IdDiarioObras?: number;
  IdDiarioObrasEquipamento?: number;
  page?: number;
  rowsPerPage?: number;
};
type LoadDiarioObraAtividadesFuncState = {
  IdDiarioObras?: number;
  IdDiarioObrasAtividades?: number;
  page?: number;
  rowsPerPage?: number;
};
type LoadDiarioObraParalisacoesFuncState = {
  IdDiarioObras?: number;
  IdDiarioObrasParalisacoes?: number;
  page?: number;
  rowsPerPage?: number;
};
type LoadDiarioObraImagensFuncState = {
  IdDiarioObras?: number;
  IdDiarioObrasImagens?: number;
  page?: number;
  rowsPerPage?: number;
};
export interface TotaisEfetivoState {
  totalGeral: number;
  totalPropria: number;
  totalTerceirizada: number;
}

export const useDiarioObraCadastroViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      getFuncionariosDropDownUseCase,
      getFuncionariosTercDropDownUseCase,
    },

    diarioObra: {
      updateDiarioObraUseCase,
      aprovarDiarioObraUseCase,
      deleteEfetivoUseCase,
      createDiarioObraUseCase,
      createEfetivoUseCase,
      getAllListagemEfetivoUseCase,
      updateEfetivoUseCase,
      createEquipamentoUseCase,
      getAllListagemEquipamentoUseCase,
      updateEquipamentoUseCase,
      deleteEquipamentoUseCase,
      getAllListagemAtividadesUseCase,
      createAtividadesUseCase,
      updateAtividadesUseCase,
      deleteAtividadesUseCase,
      createParalisacoesUseCase,
      updateParalisacoesUseCase,
      deleteParalisacoesUseCase,
      getAllListagemParalisacoesUseCase,
      createImagensUseCase,
      updateImagensUseCase,
      deleteImagensUseCase,
      downloadImagemUseCase,
      getAllListagemImagensUseCase,
      getAllListagemDiarioObrasUseCase,
    },
  } = useDependencyInjection();

  const { id } = useParams();
  const navigate = useNavigate();
  const debouncedRef = useCreateDebounceHandler(750);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [diarioObrasLista, setDiarioObrasLista] = useState<DiarioObrasLista[]>(
    [],
  );
  const [selectedDiarioObra, setSelectedDiarioObra] =
    useState<DiarioObrasLista>();
  const [IdDiarioObras, setIdDiarioObras] = useState<number>(0);
  const [read, setRead] = useState<boolean>(false);
  const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setCustomLoading } =
    usePageLoader();
  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  const filters = useDiarioObrasFilters();
  const pagination = useDiarioObrasPagination();

  //#region Efetivo
  const [openEfetivo, setOpenEfetivo] = useState<boolean>(false);
  const [efetivoDeleteModal, setEfetivoDeleteModal] = useState<boolean>(false);
  const [diarioObrasListaEfetivo, setDiarioObrasListaEfetivo] = useState<
    DiarioObrasEfetivoLista[]
  >([]);
  const [selectedDiarioObraEfetivo, setSelectedDiarioObraEfetivo] =
    useState<DiarioObrasEfetivoLista>();
  const [loadingEfetivo, setLoadingEfetivo] = useState<boolean>(false);
  const [diarioObraEfetivoDeleteModal, setDiarioObraEfetivoDeleteModal] =
    useState<boolean>(false);
  const [totaisEfetivo, setTotaisEfetivo] = useState<TotaisEfetivoState>({
    totalGeral: 0,
    totalPropria: 0,
    totalTerceirizada: 0,
  });
  const [funcionarioDropdown, setFuncionarioDropdown] = useState<
    FuncionarioDropDown[]
  >([]);
  const [funcionarioTerceirizadoDropdown, setFuncionarioTerceirizadoDropdown] =
    useState<FuncionarioTercDropDown[]>([]);
  const [modalAberto, setModalAberto] = useState<boolean>(false);
  const [linhaSelecionada, setLinhaSelecionada] = useState<{
    ids: number[];
    tipo: number;
  } | null>(null);
  const filtersEfetivo = useDiarioObrasEfetivoFilters();
  const paginationEfetivo = useDiarioObrasEfetivoPagination();

  const hookFormEfetivo = useForm<DiarioObrasEfetivoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(DiarioObrasEfetivoSchema),
    defaultValues: {
      Categoria: 0,
      Tipo: 0,
    },
  });
  //#endregion
  //#region Equipamento
  const [openEquipamento, setOpenEquipamento] = useState<boolean>(false);
  const [diarioObrasListEquipamento, setDiarioObrasListEquipamento] = useState<
    DiarioObrasEquipamentoLista[]
  >([]);
  const [selectedDiarioObraEquipamento, setSelectedDiarioObraEquipamento] =
    useState<DiarioObrasEquipamentoLista>();
  const [loadingEquipamento, setLoadingEquipamento] = useState<boolean>(false);
  const [
    diarioObraEquipamentoDeleteModal,
    setDiarioObraEquipamentoDeleteModal,
  ] = useState<boolean>(false);
  const [savingEquipamento, setSavingEquipamento] = useState<boolean>(false);

  const filtersEquipamento = useDiarioObrasEquipamentoFilters();
  const paginationEquipamento = useDiarioObrasEquipamentoPagination();

  const hookFormEquipamento = useForm<DiarioObrasEquipamentoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(DiarioObrasEquipamentoSchema),
  });
  //#endregion
  //#region Atividades Realizadas
  const [openAtividades, setOpenAtividades] = useState<boolean>(false);
  const [diarioObrasListAtividades, setDiarioObrasListAtividades] = useState<
    DiarioObrasAtividadesLista[]
  >([]);
  const [selectedDiarioObraAtividades, setSelectedDiarioObraAtividades] =
    useState<DiarioObrasAtividadesLista>();
  const [loadingAtividades, setLoadingAtividades] = useState<boolean>(false);
  const [diarioObraAtividadesDeleteModal, setDiarioObraAtividadesDeleteModal] =
    useState<boolean>(false);
  const [savingAtividades, setSavingAtividades] = useState<boolean>(false);

  const filtersAtividades = useDiarioObrasAtividadesFilters();
  const paginationAtividades = useDiarioObrasAtividadesPagination();

  const hookFormAtividades = useForm<DiarioObrasAtividadesForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(DiarioObrasAtividadesSchema),
  });
  //#endregion

  //#region Paralisacoes
  const [openParalisacoes, setOpenParalisacoes] = useState<boolean>(false);
  const [diarioObrasListParalisacoes, setDiarioObrasListParalisacoes] =
    useState<DiarioObrasParalisacoesLista[]>([]);
  const [selectedDiarioObraParalisacoes, setSelectedDiarioObraParalisacoes] =
    useState<DiarioObrasParalisacoesLista>();
  const [loadingParalisacoes, setLoadingParalisacoes] =
    useState<boolean>(false);
  const [
    diarioObraParalisacoesDeleteModal,
    setDiarioObraParalisacoesDeleteModal,
  ] = useState<boolean>(false);
  const [savingParalisacoes, setSavingParalisacoes] = useState<boolean>(false);

  const filtersParalisacoes = useDiarioObrasParalisacoesFilters();
  const paginationParalisacoes = useDiarioObrasParalisacoesPagination();

  const hookFormParalisacoes = useForm<DiarioObrasParalisacoesForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(DiarioObrasParalisacoesSchema),
  });
  //#endregion

  //#region Imagens
  const [fileUploadUrl, setFileUploadURL] = useState<string | null>(null);
  const [fileUpload, setFileUpload] = useState<File | null>(null);
  const [limparArquivo, setLimparArquivo] = useState<boolean>(false);
  const previewUrlRef = useRef<string | null>(null);
  const previewRequestRef = useRef<number>(0);

  const replacePreviewUrl = useCallback((url: string | null) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = url;
    setFileUploadURL(url);
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);
  const [openImagens, setOpenImagens] = useState<boolean>(false);
  const [diarioObrasListImagens, setDiarioObrasListImagens] = useState<
    DiarioObrasImagem[]
  >([]);
  const [selectedDiarioObraImagens, setSelectedDiarioObraImagens] =
    useState<DiarioObrasImagem>();
  const [loadingImagens, setLoadingImagens] = useState<boolean>(false);
  const [diarioObraImagensDeleteModal, setDiarioObraImagensDeleteModal] =
    useState<boolean>(false);
  const [savingImagens, setSavingImagens] = useState<boolean>(false);
  const filtersImagens = useDiarioObrasImagensFilters();
  const paginationImagens = useDiarioObrasImagensPagination();

  const hookFormImagens = useForm<DiarioObrasImagensForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(DiarioObrasImagensSchema),
  });
  //#endregion

  const hookForm = useForm<DiarioObrasForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(DiarioObrasSchema),
    defaultValues: {
      DiaPraticavel: "1",
      CondicaoTempo: "B",
    },
  });
  const { loadCentroCustoDropDown } = useDropDownLoader();
  const { idUsuario } = useUserAuthStore();

  // #region Funções de carregamento
  const loadFuncionarioDropDown = useCallback(
    async (idCargo?: number) => {
      try {
        if (idCargo) {
          const funcionarios =
            await getFuncionariosDropDownUseCase.getFuncionariosDropDown(
              undefined,
              false,
              false,
              idCargo,
            );
          setFuncionarioDropdown(funcionarios);
        } else {
          const funcionarios =
            await getFuncionariosDropDownUseCase.getFuncionariosDropDown(
              undefined,
              false,
              false,
              idCargo
            );

          setFuncionarioDropdown(funcionarios);
        }
      } catch (e: unknown) {
        console.error("Erro ao carregar funcionarios:", e);
      }
    },
    [getFuncionariosDropDownUseCase],
  );
  const loadFuncionarioTerceirizadoDropDown = useCallback(
    async (idFornecedor?: number) => {
      try {
        if (idFornecedor) {
          const funcionarios =
            await getFuncionariosTercDropDownUseCase.getFuncionariosTercDropDown(
              undefined,
              idFornecedor,
            );
          setFuncionarioTerceirizadoDropdown(funcionarios);
        } else {
          const funcionarios =
            await getFuncionariosTercDropDownUseCase.getFuncionariosTercDropDown(
              undefined,
            );

          setFuncionarioTerceirizadoDropdown(funcionarios);
        }
      } catch (e: unknown) {
        console.error("Erro ao carregar funcionarios:", e);
      }
    },
    [getFuncionariosTercDropDownUseCase],
  );

  const loadDiarioObrasEfetivoListaState = useCallback(
    async (funcState?: LoadDiarioObraFuncState) => {
      try {
        setLoadingEfetivo(true);
        const filterIdDiarioObrasEfetivo = evaluateParamValue(
          funcState?.IdDiarioObrasEfetivo,
          filtersEfetivo.IdDiarioObrasEfetivo,
        );
        const IdDiarioObras =
          funcState?.IdDiarioObras ?? filtersEfetivo.IdDiarioObras;
        const page = funcState?.page ?? paginationEfetivo.page;
        const rows = funcState?.rowsPerPage ?? paginationEfetivo.rowsPerPage;

        await getAllListagemEfetivoUseCase.getAllListagemDiariosObrasEfetivo(
          {
            IdDiarioObras: IdDiarioObras,
            IdDiarioObrasEfetivo: filterIdDiarioObrasEfetivo,
          },
          page,
          rows,
        );
        setDiarioObrasListaEfetivo(getAllListagemEfetivoUseCase.items);
        setTotaisEfetivo({
          totalGeral: getAllListagemEfetivoUseCase.totalGeral,
          totalPropria: getAllListagemEfetivoUseCase.totalPropria,
          totalTerceirizada: getAllListagemEfetivoUseCase.totalTerceirizada,
        });
        paginationEfetivo.setRowsPerPage(getAllListagemEfetivoUseCase.pageSize);
        paginationEfetivo.setTotalItems(
          getAllListagemEfetivoUseCase.totalItems,
        );
        setLoadingEfetivo(false);
        hookFormEfetivo.reset({
          IdDiarioObras: IdDiarioObras,
          IdDiarioObrasEfetivo: 0,
          Tipo: 0,
          Categoria: 0,
          Quantidade: 0,
          Descricao: "",
          IdFornecedor: null,
          IdCargo: undefined,
        });
      } catch (e: unknown) {
        throw new Error("Falha ao buscar Diario de Obras Efetivo!");
      }
    },
    [
      filtersEfetivo.IdDiarioObras,
      filtersEfetivo.IdDiarioObrasEfetivo,
      getAllListagemEfetivoUseCase,
      hookFormEfetivo,
      paginationEfetivo,
    ],
  );

  const loadDiarioObrasEquipamentoListaState = useCallback(
    async (funcState?: LoadDiarioObraEquipamentoFuncState) => {
      try {
        setLoadingEquipamento(true);
        const filterIdDiarioObrasEquipamento = evaluateParamValue(
          funcState?.IdDiarioObrasEquipamento,
          filtersEquipamento.IdDiarioObrasEquipamento,
        );
        const IdDiarioObras =
          funcState?.IdDiarioObras ?? filtersEquipamento.IdDiarioObras;
        const page = funcState?.page ?? paginationEquipamento.page;
        const rows =
          funcState?.rowsPerPage ?? paginationEquipamento.rowsPerPage;

        await getAllListagemEquipamentoUseCase.getAllListagemDiariosObrasEquipamento(
          {
            IdDiarioObras: IdDiarioObras,
            IdDiarioObrasEquipamento: filterIdDiarioObrasEquipamento,
          },
          page,
          rows,
        );
        setDiarioObrasListEquipamento(getAllListagemEquipamentoUseCase.items);
        paginationEquipamento.setRowsPerPage(
          getAllListagemEquipamentoUseCase.pageSize,
        );
        paginationEquipamento.setTotalItems(
          getAllListagemEquipamentoUseCase.totalItems,
        );
        setLoadingEquipamento(false);
        hookFormEquipamento.reset({
          IdDiarioObras: IdDiarioObras,
          IdDiarioObrasEquipamento: 0,
          Quantidade: 0,
          Descricao: "",
          Tipo: "0",
        });
      } catch (e: unknown) {
        throw new Error("Falha ao buscar Diario de Obras Equipamento!");
      }
    },
    [
      filtersEquipamento.IdDiarioObras,
      filtersEquipamento.IdDiarioObrasEquipamento,
      getAllListagemEquipamentoUseCase,
      hookFormEquipamento,
      paginationEquipamento,
    ],
  );

  const loadDiarioObrasAtividadesListaState = useCallback(
    async (funcState?: LoadDiarioObraAtividadesFuncState) => {
      try {
        setLoadingAtividades(true);
        const filterIdDiarioObrasAtividades = evaluateParamValue(
          funcState?.IdDiarioObrasAtividades,
          filtersAtividades.IdDiarioObrasAtividades,
        );
        const IdDiarioObras =
          funcState?.IdDiarioObras ?? filtersAtividades.IdDiarioObras;
        const page = funcState?.page ?? paginationAtividades.page;
        const rows = funcState?.rowsPerPage ?? paginationAtividades.rowsPerPage;

        await getAllListagemAtividadesUseCase.getAllListagemDiariosObrasAtividades(
          {
            IdDiarioObras: IdDiarioObras,
            IdDiarioObrasAtividades: filterIdDiarioObrasAtividades,
          },
          page,
          rows,
        );
        setDiarioObrasListAtividades(getAllListagemAtividadesUseCase.items);
        paginationAtividades.setRowsPerPage(
          getAllListagemAtividadesUseCase.pageSize,
        );
        paginationAtividades.setTotalItems(
          getAllListagemAtividadesUseCase.totalItems,
        );
        setLoadingAtividades(false);
        hookFormAtividades.reset({
          IdDiarioObras: IdDiarioObras,
          IdDiarioObrasAtividades: 0,
          Descricao: "",
          IdFornecedor: 0,
          Ordem: 0,
          TipoMaoDeObra: 0,
        });
      } catch (e: unknown) {
        throw new Error("Falha ao buscar Diario de Obras Atividades!");
      }
    },
    [
      filtersAtividades.IdDiarioObras,
      filtersAtividades.IdDiarioObrasAtividades,
      getAllListagemAtividadesUseCase,
      hookFormAtividades,
      paginationAtividades,
    ],
  );

  const loadDiarioObrasImagensListaState = useCallback(
    async (funcState?: LoadDiarioObraImagensFuncState) => {
      try {
        setLoadingImagens(true);
        const filterIdDiarioObrasImagens = evaluateParamValue(
          funcState?.IdDiarioObrasImagens,
          filtersImagens.IdDiarioObrasImagens,
        );
        const IdDiarioObras =
          funcState?.IdDiarioObras ?? filtersImagens.IdDiarioObras;
        const page = funcState?.page ?? paginationImagens.page;
        const rows = funcState?.rowsPerPage ?? paginationImagens.rowsPerPage;

        await getAllListagemImagensUseCase.getAllListagemDiariosObrasImagens(
          {
            IdDiarioObras: IdDiarioObras,
            IdDiarioObrasImagens: filterIdDiarioObrasImagens,
          },
          page,
          rows,
        );
        setDiarioObrasListImagens(getAllListagemImagensUseCase.items);
        paginationImagens.setRowsPerPage(getAllListagemImagensUseCase.pageSize);
        paginationImagens.setTotalItems(
          getAllListagemParalisacoesUseCase.totalItems,
        );

        setLoadingImagens(false);
        hookFormImagens.reset({
          IdDiarioObras: IdDiarioObras,
          IdDiarioObrasImagens: 0,
          DescricaoImagem: "",
          ExtensaoImagem: "",
          Nome: "",
          Ordem: 0,
        });
        setFileUpload(null);
        setLimparArquivo(false);
        replacePreviewUrl(null);
      } catch (e: unknown) {
        throw new Error("Falha ao buscar Diario de Obras Paralisacoes!");
      }
    },
    [
      filtersImagens.IdDiarioObras,
      filtersImagens.IdDiarioObrasImagens,
      getAllListagemImagensUseCase,
      getAllListagemParalisacoesUseCase.totalItems,
      hookFormImagens,
      paginationImagens,
      replacePreviewUrl,
    ],
  );

  const loadDiarioObrasParalisacoesListaState = useCallback(
    async (funcState?: LoadDiarioObraParalisacoesFuncState) => {
      try {
        setLoadingParalisacoes(true);
        const filterIdDiarioObrasParalisacoes = evaluateParamValue(
          funcState?.IdDiarioObrasParalisacoes,
          filtersParalisacoes.IdDiarioObrasParalisacoes,
        );
        const IdDiarioObras =
          funcState?.IdDiarioObras ?? filtersParalisacoes.IdDiarioObras;
        const page = funcState?.page ?? paginationParalisacoes.page;
        const rows =
          funcState?.rowsPerPage ?? paginationParalisacoes.rowsPerPage;

        await getAllListagemParalisacoesUseCase.getAllListagemDiariosObrasParalisacoes(
          {
            IdDiarioObras: IdDiarioObras,
            IdDiarioObrasParalisacoes: filterIdDiarioObrasParalisacoes,
          },
          page,
          rows,
        );
        setDiarioObrasListParalisacoes(getAllListagemParalisacoesUseCase.items);
        paginationParalisacoes.setRowsPerPage(
          getAllListagemParalisacoesUseCase.pageSize,
        );
        paginationParalisacoes.setTotalItems(
          getAllListagemParalisacoesUseCase.totalItems,
        );

        setLoadingParalisacoes(false);
        hookFormParalisacoes.reset({
          IdDiarioObras: IdDiarioObras,
          IdDiarioObrasParalisacoes: 0,
          Assunto: "",
          HorarioFim: "",
          HorarioInicio: "",
          Motivo: "",
          TempoParalisacao: "",
        });
      } catch (e: unknown) {
        throw new Error("Falha ao buscar Diario de Obras Paralisacoes!");
      }
    },
    [
      filtersParalisacoes.IdDiarioObras,
      filtersParalisacoes.IdDiarioObrasParalisacoes,
      getAllListagemParalisacoesUseCase,
      hookFormParalisacoes,
      paginationParalisacoes,
    ],
  );

  const loadDiarioObrasListaState = useCallback(
    async (funcState?: LoadDiarioObraFuncState) => {
      try {
        const filterID = evaluateParamValue(
          funcState?.IdDiarioObras,
          IdDiarioObras,
        );

        await getAllListagemDiarioObrasUseCase.getAllListagemDiariosObras(
          {
            IdDiarioObras: filterID,
          },
          1,
          1,
        );

        const diario =
          getAllListagemDiarioObrasUseCase.items &&
            getAllListagemDiarioObrasUseCase.items.length > 0
            ? getAllListagemDiarioObrasUseCase.items[0]
            : null;
        if (diario) {
          setSelectedDiarioObra(getAllListagemDiarioObrasUseCase.items[0]);
          setRead(diario.Status === 1 ? true : false);
          hookForm.reset({
            IdDiarioObras: diario.IdDiarioObras,
            IdCentroCusto: diario.IdCentroCusto,
            IdCliente: diario.IdCliente,
            IdClienteCentroCusto: diario.IdClienteCentroCusto,
            IdFuncionarioResponsavel: diario.IdFuncionarioResponsavel,
            IdFuncionarioAprovador: diario.IdFuncionarioAprovador,
            StatusNome:
              diario.Status === 0
                ? "Em Elaboração"
                : diario.Status === 1
                  ? "Aprovado"
                  : diario.Status === 2
                    ? "Liberado para aprovação "
                    : "Aprovado",
            Status: diario.Status,
            NroDiario: diario.NroDiario,
            NumeroDocumento: diario.NumeroDocumento,
            ResponsavelPor: diario.ResponsavelPor,
            AprovadoPor: diario.AprovadoPor,
            CondicaoTempo: diario.CondicaoTempo,
            Observacao: diario.Observacao,
            JustificativaDia: diario.JustificativaDia,
            DataCadastro: diario.DataCadastro
              ? new Date(diario.DataCadastro)
              : null,
            DataAlteracao: diario.DataAlteracao
              ? new Date(diario.DataAlteracao)
              : null,
            DiaPraticavel: diario.DiaPraticavel?.toString() ?? null,
            AssinaturaCliente: diario.AssinaturaCliente,
            AssinaturaConstrutora: diario.AssinaturaConstrutora,
            ResponsavelCadastrado:
              !diario.IdFuncionarioResponsavel && !!diario.ResponsavelPor,
            AprovadoPorCadastrado:
              !diario.IdFuncionarioAprovador && !!diario.AprovadoPor,

            HorarioTrabalhoDe: diario.HorarioTrabalhoDe,
            HorarioTrabalhoAte: diario.HorarioTrabalhoAte,
            HorarioAlmocoDe: diario.HorarioAlmocoDe,
            HorarioAlmocoAte: diario.HorarioAlmocoAte,
          });
        }
      } catch (e: unknown) {
        throw new Error("Falha ao buscar Diario de Obras!");
      }
    },
    [IdDiarioObras, getAllListagemDiarioObrasUseCase, hookForm],
  );
  //#endregion

  // #region useEffect

  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      if (id) {
        setIdDiarioObras(Number(id));
        jobs.push(loadDiarioObrasListaState({ IdDiarioObras: Number(id) }));
        jobs.push(
          loadDiarioObrasEfetivoListaState({ IdDiarioObras: Number(id) }),
        );
        jobs.push(
          loadDiarioObrasEquipamentoListaState({ IdDiarioObras: Number(id) }),
        );
        jobs.push(
          loadDiarioObrasAtividadesListaState({ IdDiarioObras: Number(id) }),
        );
        jobs.push(
          loadDiarioObrasImagensListaState({ IdDiarioObras: Number(id) }),
        );
        jobs.push(
          loadDiarioObrasParalisacoesListaState({ IdDiarioObras: Number(id) }),
        );
        jobs.push(loadFuncionarioDropDown());
      }
      await loadCentroCustoDropDown((list) =>
        useDiarioObrasFilters.setState({ centroDeCustoLista: list }),
      );
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      const jobs = [];
      if (id) {
        setIdDiarioObras(Number(id));
        jobs.push(loadDiarioObrasListaState({ IdDiarioObras: Number(id) }));
        jobs.push(
          loadDiarioObrasEfetivoListaState({ IdDiarioObras: Number(id) }),
        );
        jobs.push(
          loadDiarioObrasEquipamentoListaState({ IdDiarioObras: Number(id) }),
        );
        jobs.push(
          loadDiarioObrasAtividadesListaState({ IdDiarioObras: Number(id) }),
        );
        jobs.push(
          loadDiarioObrasImagensListaState({ IdDiarioObras: Number(id) }),
        );
        jobs.push(
          loadDiarioObrasParalisacoesListaState({ IdDiarioObras: Number(id) }),
        );
        jobs.push(loadFuncionarioDropDown());
      }
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [
    id,
    loadDiarioObrasListaState,
    loadDiarioObrasEfetivoListaState,
    loadDiarioObrasEquipamentoListaState,
    loadDiarioObrasAtividadesListaState,
    loadDiarioObrasImagensListaState,
    loadDiarioObrasParalisacoesListaState,
    loadFuncionarioDropDown,
    pushErrorNotification,
  ]);
  //#endregion

  // #region Eventos

  const handleDiarioObraSubmit = useCallback(
    async (form: DiarioObrasForm) => {
      try {
        if (IdDiarioObras) {
          if (form.Status === 1) {
            const diariosObrasIds: number[] = [IdDiarioObras];
            await aprovarDiarioObraUseCase.aprovarDiarioObra(
              idUsuario,
              diariosObrasIds ?? [],
            );
            setRead(true);
          } else {
            await updateDiarioObraUseCase.updateDiarioObra(
              IdDiarioObras,
              form.IdCentroCusto ?? null,
              form.IdCliente ?? null,
              form.IdClienteCentroCusto ?? null,
              form.IdFuncionarioResponsavel ?? null,
              form.IdFuncionarioAprovador ?? null,
              form.NumeroDocumento ?? null,
              form.DataCadastro ?? null,
              form.ResponsavelPor ?? null,
              form.AprovadoPor ?? null,
              form.CondicaoTempo ?? null,
              Number(form.DiaPraticavel) ?? null,
              form.Observacao ?? null,
              form.Status ?? 0,
              form.JustificativaDia ?? null,
              form.AssinaturaCliente ?? null,
              form.AssinaturaConstrutora ?? null,
              form.NroDiario ?? null,
              form.DataAlteracao ?? null,
              form.HorarioTrabalhoDe ?? null,
              form.HorarioTrabalhoAte ?? null,
              form.HorarioAlmocoDe ?? null,
              form.HorarioAlmocoAte ?? null
            );
          }
          pushInfoNotification(
            "Sucesso",
            "Diario de obra atualizado com sucesso!",
            { isAutoFinish: true },
          );
        } else {
          const idItem = await createDiarioObraUseCase.createDiarioObra(
            form.IdCentroCusto ?? null,
            form.IdCliente ?? null,
            form.IdClienteCentroCusto ?? null,
            form.IdFuncionarioResponsavel ?? null,
            form.IdFuncionarioAprovador ?? null,
            form.NumeroDocumento ?? null,
            form.DataCadastro ?? null,
            form.ResponsavelPor ?? null,
            form.AprovadoPor ?? null,
            form.CondicaoTempo ?? null,
            Number(form.DiaPraticavel) ?? null,
            form.Observacao ?? null,
            form.Status ?? 0,
            form.JustificativaDia ?? null,
            form.AssinaturaCliente ?? null,
            form.AssinaturaConstrutora ?? null,
            form.NroDiario ?? null,
            form.DataAlteracao ?? null,
            form.HorarioTrabalhoDe ?? null,
            form.HorarioTrabalhoAte ?? null,
            form.HorarioAlmocoDe ?? null,
            form.HorarioAlmocoAte ?? null
          );
          setIdDiarioObras(Number(idItem));
          pushInfoNotification(
            "Sucesso",
            "Diario de obra criado com sucesso!",
            { isAutoFinish: true },
          );
          loadDiarioObrasListaState({ IdDiarioObras: Number(idItem) });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar/atualizar!"), { isAutoFinish: true },
        );
      } finally {
        setSaving(false);
      }
    },
    [
      IdDiarioObras,
      aprovarDiarioObraUseCase,
      createDiarioObraUseCase,
      idUsuario,
      loadDiarioObrasListaState,
      pushErrorNotification,
      pushInfoNotification,
      updateDiarioObraUseCase,
    ],
  );

  //#region Efetivo
  const handleEfeivo = useCallback(async () => {
    if (IdDiarioObras > 0 || selectedDiarioObra) {
      setEfetivoDeleteModal(false);
      setOpenEfetivo(true);
      setSelectedDiarioObraEfetivo(undefined);
      let id =
        IdDiarioObras > 0 ? IdDiarioObras : selectedDiarioObra?.IdDiarioObras;
      hookFormEfetivo.reset({
        IdDiarioObras: id,
        IdDiarioObrasEfetivo: 0,
        Tipo: 0,
        Categoria: 0,
        Quantidade: 0,
        Descricao: "",
        IdFornecedor: null,
        IdCargo: undefined,
      });
      setLoadingEfetivo(false);
    } else {
      setEfetivoDeleteModal(false);
      setOpenEfetivo(false);
      return pushWarnNotification(
        "Atenção",
        "Selecione um diário de Obra ou salve um novo !",
      );
    }
  }, [
    IdDiarioObras,
    hookFormEfetivo,
    pushWarnNotification,
    selectedDiarioObra,
  ]);

  const handleEfetivoSubmit = useCallback(
    async (form: DiarioObrasEfetivoForm) => {
      try {
        if (IdDiarioObras && IdDiarioObras > 0) {
          if (form.IdDiarioObrasEfetivo) {
            await updateEfetivoUseCase.updateDiarioObraEfetivo(
              IdDiarioObras,
              form.IdDiarioObrasEfetivo,
              form.IdCargo,
              form.Tipo ?? null,
              form.Categoria ?? null,
              form.Quantidade ?? null,
              form.Descricao ?? null,
              form.IdFornecedor ?? null,
              form.IdsFuncionarios ?? [],
            );
            setOpenEfetivo(false);
            pushInfoNotification(
              "Sucesso",
              "Diario de obra Efetivo atualizado com sucesso!",
              { isAutoFinish: true },
            );
          } else {
            await createEfetivoUseCase.createDiarioObraEfetivo(
              IdDiarioObras,
              form.IdCargo,
              form.Tipo ?? null,
              form.Categoria ?? null,
              form.Quantidade ?? null,
              form.Descricao ?? null,
              form.IdFornecedor ?? null,
              form.IdsFuncionarios ?? [],
            );
            pushInfoNotification(
              "Sucesso",
              "Diario de obra Efetivo criado com sucesso!",
              { isAutoFinish: true },
            );
            setOpenEfetivo(false);
          }
          await loadDiarioObrasEfetivoListaState({
            IdDiarioObras: IdDiarioObras,
          });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar Diario de Obras Efetivos!"), { isAutoFinish: true },
        );
      } finally {
        setSaving(false);
      }
    },
    [
      IdDiarioObras,
      createEfetivoUseCase,
      loadDiarioObrasEfetivoListaState,
      pushErrorNotification,
      pushInfoNotification,
      updateEfetivoUseCase,
    ],
  );

  const handleEdicaoEfetivoOpenState = useCallback(
    (
      IdDiarioObrasEfetivo: number | null,
      openState: boolean,
      openAnalise: boolean,
    ) => {
      if (openState) setOpenEfetivo(true);

      if (IdDiarioObrasEfetivo) {
        const itemParaEditar = diarioObrasListaEfetivo.find(
          (x) => x.IdDiarioObrasEfetivo === IdDiarioObrasEfetivo,
        );
        setSelectedDiarioObraEfetivo(itemParaEditar);
      } else {
        hookFormEfetivo.reset();
        setSelectedDiarioObraEfetivo(undefined);
      }
    },
    [diarioObrasListaEfetivo, hookFormEfetivo],
  );
  const handleEfetivoFuncionarioOpenState = useCallback(
    (IdDiarioObrasEfetivo: number | null, openState: boolean) => {
      if (openState) setModalAberto(true);

      if (IdDiarioObrasEfetivo) {
        const itemParaEditar = diarioObrasListaEfetivo.find(
          (x) => x.IdDiarioObrasEfetivo === IdDiarioObrasEfetivo,
        );
        if (itemParaEditar) {
          if (itemParaEditar.Tipo === 1 && itemParaEditar.IdFornecedor) {
            loadFuncionarioTerceirizadoDropDown(itemParaEditar.IdFornecedor);
          } else if (itemParaEditar.Tipo === 0 && itemParaEditar.IdCargo) {
            loadFuncionarioDropDown(itemParaEditar.IdCargo);
          }
        }

        setLinhaSelecionada({
          ids: itemParaEditar?.IdsFuncionarios || [],
          tipo: itemParaEditar?.Tipo ?? 0,
        });
      }
    },
    [
      diarioObrasListaEfetivo,
      loadFuncionarioTerceirizadoDropDown,
      loadFuncionarioDropDown,
    ],
  );

  const handleDeleteDiarioEfetivoModalOpenState = useCallback(
    (IdDiarioObrasEfetivo: number | null, openState: boolean) => {
      if (openState) {
        if (IdDiarioObras && diarioObrasListaEfetivo && IdDiarioObrasEfetivo) {
          setSelectedDiarioObraEfetivo(
            diarioObrasListaEfetivo.find(
              (x) => x.IdDiarioObrasEfetivo === IdDiarioObrasEfetivo,
            ),
          );
        } else {
          setSelectedDiarioObraEfetivo(undefined);
        }
      }
      setDiarioObraEfetivoDeleteModal(openState);
    },
    [IdDiarioObras, diarioObrasListaEfetivo],
  );

  const handleDeleteConfirmaDiarioEfetivo = useCallback(async () => {
    if (selectedDiarioObraEfetivo) {
      try {
        setSaving(true);
        await deleteEfetivoUseCase.deleteDiarioObraEfetivo(
          selectedDiarioObraEfetivo.IdDiarioObras,
          selectedDiarioObraEfetivo.IdDiarioObrasEfetivo,
        );
        await loadDiarioObrasEfetivoListaState({
          IdDiarioObras: IdDiarioObras,
        });
        pushInfoNotification(
          "Sucesso",
          "Diario de obra Efetivo atualizado com sucesso!",
          { isAutoFinish: true },
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"),
        );
      } finally {
        setSaving(false);
        setDiarioObraEfetivoDeleteModal(false);
      }
    }
  }, [
    selectedDiarioObraEfetivo,
    deleteEfetivoUseCase,
    loadDiarioObrasEfetivoListaState,
    IdDiarioObras,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  //#endregion
  //#endregion

  //#region Equipamento
  const handleEquipamento = useCallback(async () => {
    if (IdDiarioObras > 0 || selectedDiarioObra) {
      setDiarioObraEquipamentoDeleteModal(false);
      setOpenEquipamento(true);
      setSelectedDiarioObraEquipamento(undefined);
      let id =
        IdDiarioObras > 0 ? IdDiarioObras : selectedDiarioObra?.IdDiarioObras;
      hookFormEquipamento.reset({
        IdDiarioObras: id,
        IdDiarioObrasEquipamento: 0,
        Tipo: "0",
      });
    } else {
      setDiarioObraEquipamentoDeleteModal(false);
      setOpenEquipamento(false);
      return pushWarnNotification(
        "Atenção",
        "Selecione um diário de Obra ou salve um novo !",
      );
    }
  }, [
    IdDiarioObras,
    hookFormEquipamento,
    pushWarnNotification,
    selectedDiarioObra,
  ]);

  const handleEquipamentoSubmit = useCallback(
    async (form: DiarioObrasEquipamentoForm) => {
      try {
        if (IdDiarioObras && IdDiarioObras > 0) {
          if (form.IdDiarioObrasEquipamento) {
            await updateEquipamentoUseCase.updateDiarioObraEquipamento(
              form.IdDiarioObrasEquipamento,
              IdDiarioObras,
              form.IdMaquinaEquip ?? null,
              form.Descricao ?? null,
              form.Tipo ?? null,
              form.Quantidade ?? null,
            );
            setOpenEquipamento(false);
          } else {
            await createEquipamentoUseCase.createDiarioObraEquipamento(
              IdDiarioObras,
              form.IdMaquinaEquip ?? null,
              form.Descricao ?? null,
              form.Tipo ?? null,
              form.Quantidade ?? null,
            );
            pushInfoNotification(
              "Sucesso",
              "Diario de obra Equipamento criado com sucesso!",
              { isAutoFinish: true },
            );
          }
          loadDiarioObrasEquipamentoListaState({
            IdDiarioObras: IdDiarioObras,
          });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar Diario de Obras Equipamento!"), { isAutoFinish: true },
        );
      } finally {
        setOpenEquipamento(false);
        setSaving(false);
      }
    },
    [
      IdDiarioObras,
      createEquipamentoUseCase,
      loadDiarioObrasEquipamentoListaState,
      pushErrorNotification,
      pushInfoNotification,
      updateEquipamentoUseCase,
    ],
  );

  const handleEdicaoEquipamentoOpenState = useCallback(
    (
      IdDiarioObrasEquipamento: number | null,
      openState: boolean,
      openAnalise: boolean,
    ) => {
      if (openState) setOpenEquipamento(true);

      if (IdDiarioObrasEquipamento) {
        const itemParaEditar = diarioObrasListEquipamento.find(
          (x) => x.IdDiarioObrasEquipamento === IdDiarioObrasEquipamento,
        );
        setSelectedDiarioObraEquipamento(itemParaEditar);
      } else {
        hookFormEquipamento.reset();
      }
    },
    [diarioObrasListEquipamento, hookFormEquipamento],
  );

  const handleDeleteDiarioEquipamentoModalOpenState = useCallback(
    (IdDiarioObrasEquipamento: number | null, openState: boolean) => {
      if (openState) {
        if (
          IdDiarioObras &&
          diarioObrasListEquipamento &&
          IdDiarioObrasEquipamento
        ) {
          setSelectedDiarioObraEquipamento(
            diarioObrasListEquipamento.find(
              (x) => x.IdDiarioObrasEquipamento === IdDiarioObrasEquipamento,
            ),
          );
        } else {
          setSelectedDiarioObraEquipamento(undefined);
        }
      }
      setDiarioObraEquipamentoDeleteModal(openState);
    },
    [IdDiarioObras, diarioObrasListEquipamento],
  );

  const handleDeleteConfirmaDiarioEquipamento = useCallback(async () => {
    if (selectedDiarioObraEquipamento) {
      try {
        setSavingEquipamento(true);
        await deleteEquipamentoUseCase.deleteDiarioObraEquipamento(
          IdDiarioObras ?? null,
          selectedDiarioObraEquipamento.IdDiarioObrasEquipamento,
        );
        await loadDiarioObrasEquipamentoListaState({
          IdDiarioObras: IdDiarioObras,
        });
        pushInfoNotification(
          "Sucesso",
          "Diario de obra Equipamento atualizado com sucesso!",
          { isAutoFinish: true },
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"), { isAutoFinish: true },
        );
      } finally {
        setSavingEquipamento(false);
        setDiarioObraEquipamentoDeleteModal(false);
      }
    }
  }, [
    selectedDiarioObraEquipamento,
    deleteEquipamentoUseCase,
    IdDiarioObras,
    loadDiarioObrasEquipamentoListaState,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  //#endregion
  //#endregion

  //#region Atividades
  const handleAtividades = useCallback(async () => {
    if (IdDiarioObras > 0 || selectedDiarioObra) {
      setDiarioObraAtividadesDeleteModal(false);
      setOpenAtividades(true);
      setSelectedDiarioObraAtividades(undefined);
      let id =
        IdDiarioObras > 0 ? IdDiarioObras : selectedDiarioObra?.IdDiarioObras;
      hookFormAtividades.reset({
        IdDiarioObras: id,
        IdDiarioObrasAtividades: 0,
        TipoMaoDeObra: 0,
      });
    } else {
      setDiarioObraAtividadesDeleteModal(false);
      setOpenAtividades(false);
      setSelectedDiarioObraAtividades(undefined);
      return pushWarnNotification(
        "Atenção",
        "Selecione um diário de Obra ou salve um novo !",
      );
    }
  }, [
    IdDiarioObras,
    hookFormAtividades,
    pushWarnNotification,
    selectedDiarioObra,
  ]);

  const handleAtividadesSubmit = useCallback(
    async (form: DiarioObrasAtividadesForm) => {
      try {
        if (IdDiarioObras && IdDiarioObras > 0) {
          if (form.IdDiarioObrasAtividades) {
            await updateAtividadesUseCase.updateDiarioObraAtividade(
              form.IdDiarioObrasAtividades,
              IdDiarioObras,
              form.Descricao ?? null,
              form.IdFornecedor ?? null,
              form.TipoMaoDeObra ?? null,
              form.Ordem ?? null,
            );
          } else {
            await createAtividadesUseCase.createDiarioObraAtividades(
              IdDiarioObras,
              form.Descricao ?? null,
              form.IdFornecedor ?? null,
              form.TipoMaoDeObra ?? null,
              form.Ordem ?? null,
            );
            pushInfoNotification(
              "Sucesso",
              "Diario de obra Atividades criado com sucesso!",
              { isAutoFinish: true },
            );
          }
          loadDiarioObrasAtividadesListaState({ IdDiarioObras: IdDiarioObras });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar Diario de Obras Atividades!"),
          { isAutoFinish: true },
        );
      } finally {
        setSaving(false);
        setOpenAtividades(false);
      }
    },
    [
      IdDiarioObras,
      createAtividadesUseCase,
      loadDiarioObrasAtividadesListaState,
      pushErrorNotification,
      pushInfoNotification,
      updateAtividadesUseCase,
    ],
  );

  const handleEdicaoAtividadesOpenState = useCallback(
    (
      IdDiarioObrasAtividades: number | null,
      openState: boolean,
      openAnalise: boolean,
    ) => {
      if (openState) setOpenAtividades(true);

      if (IdDiarioObrasAtividades) {
        const itemParaEditar = diarioObrasListAtividades.find(
          (x) => x.IdDiarioObrasAtividades === IdDiarioObrasAtividades,
        );
        setSelectedDiarioObraAtividades(itemParaEditar);
      } else {
        hookFormAtividades.reset();
      }
    },
    [diarioObrasListAtividades, hookFormAtividades],
  );

  const handleDeleteDiarioAtividadesModalOpenState = useCallback(
    (IdDiarioObrasAtividades: number | null, openState: boolean) => {
      if (openState) {
        if (
          IdDiarioObras &&
          diarioObrasListAtividades &&
          IdDiarioObrasAtividades
        ) {
          setSelectedDiarioObraAtividades(
            diarioObrasListAtividades.find(
              (x) => x.IdDiarioObrasAtividades === IdDiarioObrasAtividades,
            ),
          );
        } else {
          setSelectedDiarioObraAtividades(undefined);
        }
      }
      setDiarioObraAtividadesDeleteModal(openState);
    },
    [IdDiarioObras, diarioObrasListAtividades],
  );

  const handleDeleteConfirmaDiarioAtividades = useCallback(async () => {
    if (selectedDiarioObraAtividades) {
      try {
        setSavingAtividades(true);
        await deleteAtividadesUseCase.deleteDiarioObraAtividades(
          IdDiarioObras ?? null,
          selectedDiarioObraAtividades.IdDiarioObrasAtividades,
        );
        await loadDiarioObrasAtividadesListaState({
          IdDiarioObras: IdDiarioObras,
        });
        pushInfoNotification(
          "Sucesso",
          "Diario de obra Atividades atualizado com sucesso!",
          { isAutoFinish: true },
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"),
          { isAutoFinish: true },
        );
      } finally {
        setSavingAtividades(false);
        setDiarioObraAtividadesDeleteModal(false);
      }
    }
  }, [
    selectedDiarioObraAtividades,
    deleteAtividadesUseCase,
    IdDiarioObras,
    loadDiarioObrasAtividadesListaState,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  //#endregion

  //#region Paralisacoes
  const handleParalisacoes = useCallback(async () => {
    if (IdDiarioObras > 0 || selectedDiarioObra) {
      setDiarioObraParalisacoesDeleteModal(false);
      setOpenParalisacoes(true);
      setSelectedDiarioObraParalisacoes(undefined);
      let id =
        IdDiarioObras > 0 ? IdDiarioObras : selectedDiarioObra?.IdDiarioObras;
      hookFormParalisacoes.reset({
        IdDiarioObras: id,
        IdDiarioObrasParalisacoes: 0,
      });
    } else {
      setDiarioObraParalisacoesDeleteModal(false);
      setOpenParalisacoes(false);
      setSelectedDiarioObraParalisacoes(undefined);
      return pushWarnNotification(
        "Atenção",
        "Selecione um diário de Obra ou salve um novo !",
      );
    }
  }, [
    IdDiarioObras,
    hookFormParalisacoes,
    pushWarnNotification,
    selectedDiarioObra,
  ]);

  const handleParalisacoesSubmit = useCallback(
    async (form: DiarioObrasParalisacoesForm) => {
      try {
        if (IdDiarioObras && IdDiarioObras > 0) {
          if (form.IdDiarioObrasParalisacoes) {
            await updateParalisacoesUseCase.updateDiarioObraParalisacoes(
              form.IdDiarioObrasParalisacoes,
              IdDiarioObras,
              form.HorarioInicio ?? null,
              form.HorarioFim ?? null,
              form.Motivo ?? null,
              form.Assunto ?? null,
              form.TempoParalisacao ?? null,
            );
            setOpenParalisacoes(false);
            pushInfoNotification(
              "Sucesso",
              "Diario de obra Paralisacoes atualização com sucesso!",
              { isAutoFinish: true },
            );
          } else {
            await createParalisacoesUseCase.createDiarioObraParalisacoes(
              IdDiarioObras,
              form.HorarioInicio ?? null,
              form.HorarioFim ?? null,
              form.Motivo ?? null,
              form.Assunto ?? null,
              form.TempoParalisacao ?? null,
            );
            pushInfoNotification(
              "Sucesso",
              "Diario de obra Paralisacoes criado com sucesso!",
              { isAutoFinish: true },
            );
          }
          loadDiarioObrasParalisacoesListaState({
            IdDiarioObras: IdDiarioObras,
          });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar Diario de Obras Atividades!"), { isAutoFinish: true },
        );
      } finally {
        setSavingParalisacoes(false);
        setOpenParalisacoes(false);
        setSaving(false);
      }
    },
    [
      IdDiarioObras,
      createParalisacoesUseCase,
      loadDiarioObrasParalisacoesListaState,
      pushErrorNotification,
      pushInfoNotification,
      updateParalisacoesUseCase,
    ],
  );

  const handleEdicaoParalisacoesOpenState = useCallback(
    (
      IdDiarioObrasParalisacoes: number | null,
      openState: boolean,
      openAnalise: boolean,
    ) => {
      if (openState) setOpenParalisacoes(true);

      if (IdDiarioObrasParalisacoes) {
        const itemParaEditar = diarioObrasListParalisacoes.find(
          (x) => x.IdDiarioObrasParalisacoes === IdDiarioObrasParalisacoes,
        );
        setSelectedDiarioObraParalisacoes(itemParaEditar);
        if (itemParaEditar) {
          hookFormParalisacoes.reset({
            Assunto: itemParaEditar.Assunto,
            IdDiarioObras: itemParaEditar.IdDiarioObras,
            IdDiarioObrasParalisacoes: itemParaEditar.IdDiarioObrasParalisacoes,
            HorarioFim: itemParaEditar.HorarioFim,
            HorarioInicio: itemParaEditar.HorarioInicio,
            Motivo: itemParaEditar.Motivo,
            TempoParalisacao: itemParaEditar.TempoParalisacao,
          });
        } else {
          hookFormParalisacoes.reset();
        }
      } else {
        hookFormParalisacoes.reset();
      }
    },
    [diarioObrasListParalisacoes, hookFormParalisacoes],
  );

  const handleDeleteDiarioParalisacoesModalOpenState = useCallback(
    (IdDiarioObrasParalisacoes: number | null, openState: boolean) => {
      if (openState) {
        if (
          IdDiarioObras &&
          diarioObrasListParalisacoes &&
          IdDiarioObrasParalisacoes
        ) {
          setSelectedDiarioObraParalisacoes(
            diarioObrasListParalisacoes.find(
              (x) => x.IdDiarioObrasParalisacoes === IdDiarioObrasParalisacoes,
            ),
          );
        } else {
          setSelectedDiarioObraParalisacoes(undefined);
        }
      }
      setDiarioObraParalisacoesDeleteModal(openState);
    },
    [IdDiarioObras, diarioObrasListParalisacoes],
  );

  const handleDeleteConfirmaDiarioParalisacoes = useCallback(async () => {
    if (selectedDiarioObraParalisacoes) {
      try {
        setSavingParalisacoes(true);
        await deleteParalisacoesUseCase.deleteDiarioObraParalisacoes(
          IdDiarioObras ?? null,
          selectedDiarioObraParalisacoes.IdDiarioObrasParalisacoes,
        );
        await loadDiarioObrasParalisacoesListaState({
          IdDiarioObras: IdDiarioObras,
        });
        pushInfoNotification(
          "Sucesso",
          "Diario de obra Paralisacoes deletado com sucesso!",
          { isAutoFinish: true },
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"),
          { isAutoFinish: true },
        );
      } finally {
        setSavingParalisacoes(false);
        setDiarioObraParalisacoesDeleteModal(false);
      }
    }
  }, [
    selectedDiarioObraParalisacoes,
    deleteParalisacoesUseCase,
    IdDiarioObras,
    loadDiarioObrasParalisacoesListaState,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  //#endregion
  //#region Imagens
  const handleImagens = useCallback(async () => {
    if (IdDiarioObras > 0 || selectedDiarioObra) {
      setDiarioObraImagensDeleteModal(false);
      setOpenImagens(true);
      setSelectedDiarioObraImagens(undefined);
      let id =
        IdDiarioObras > 0 ? IdDiarioObras : selectedDiarioObra?.IdDiarioObras;
      setFileUpload(null);
      setLimparArquivo(false);
      replacePreviewUrl(null);
      hookFormImagens.reset({
        IdDiarioObras: id,
        IdDiarioObrasImagens: 0,
      });
    } else {
      setDiarioObraImagensDeleteModal(false);
      setOpenImagens(false);
      setFileUpload(null);
      setLimparArquivo(false);
      replacePreviewUrl(null);
      setSelectedDiarioObraImagens(undefined);
      return pushWarnNotification(
        "Atenção",
        "Selecione um diário de Obra ou salve um novo !",
      );
    }
  }, [
    IdDiarioObras,
    hookFormImagens,
    pushWarnNotification,
    replacePreviewUrl,
    selectedDiarioObra,
  ]);

  const handleImagensSubmit = useCallback(
    async (form: DiarioObrasImagensForm) => {
      try {
        let nomeFinal = form.Nome;
        let extensaoFinal = form.ExtensaoImagem;
        if (fileUpload) {
          FileUtils.validateFileSize(fileUpload);
          nomeFinal = fileUpload.name;
          const lastDotIndex = fileUpload.name.lastIndexOf(".");
          if (lastDotIndex !== -1) {
            extensaoFinal = fileUpload.name.substring(lastDotIndex);
          }
        }

        const limparArquivoFinal = limparArquivo && !fileUpload;
        if (limparArquivoFinal) {
          nomeFinal = null;
          extensaoFinal = null;
        }

        if (IdDiarioObras && IdDiarioObras > 0) {
          if (form.IdDiarioObrasImagens) {
            await updateImagensUseCase.updateDiarioObraImagens(
              form.IdDiarioObrasImagens,
              IdDiarioObras,
              form.DescricaoImagem ?? null,
              fileUpload,
              extensaoFinal ?? null,
              nomeFinal ?? null,
              form.Ordem ?? null,
              limparArquivoFinal,
            );
            pushInfoNotification(
              "Sucesso",
              "Diario de obra Imagens atualizado com sucesso!",
              { isAutoFinish: true },
            );
          } else {
            await createImagensUseCase.createDiarioObraImagens(
              IdDiarioObras,
              form.DescricaoImagem ?? null,
              fileUpload,
              extensaoFinal ?? null,
              nomeFinal ?? null,
              form.Ordem ?? null,
            );
            pushInfoNotification(
              "Sucesso",
              "Diario de obra Imagens criado com sucesso!",
              { isAutoFinish: true },
            );
          }

          await loadDiarioObrasImagensListaState({
            IdDiarioObras: IdDiarioObras,
          });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar Imagem!"),
          { isAutoFinish: true },
        );
      } finally {
        setOpenImagens(false);
        setSavingImagens(false);
        setSaving(false);
      }
    },
    [
      IdDiarioObras,
      createImagensUseCase,
      fileUpload,
      limparArquivo,
      loadDiarioObrasImagensListaState,
      pushErrorNotification,
      pushInfoNotification,
      updateImagensUseCase,
    ],
  );

  const handleEdicaoImagensOpenState = useCallback(
    async (IdDiarioObrasImagens: number | null, openState: boolean) => {
      if (openState) setOpenImagens(true);

      if (IdDiarioObrasImagens) {
        const itemParaEditar = diarioObrasListImagens.find(
          (x) => x.IdDiarioObrasImagens === IdDiarioObrasImagens,
        );

        setSelectedDiarioObraImagens(itemParaEditar);
        setFileUpload(null);
        setLimparArquivo(false);
        replacePreviewUrl(null);
        if (itemParaEditar) {
          hookFormImagens.reset({
            DescricaoImagem: itemParaEditar.DescricaoImagem,
            IdDiarioObras: itemParaEditar.IdDiarioObras,
            IdDiarioObrasImagens: itemParaEditar.IdDiarioObrasImagens,
            ExtensaoImagem: itemParaEditar.ExtensaoImagem,
            Nome: itemParaEditar.Nome,
            Ordem: itemParaEditar.Ordem,
          });

          if (itemParaEditar.Nome) {
            const requestId = ++previewRequestRef.current;
            try {
              const blob = await downloadImagemUseCase.getDiarioObraImagemBlob(
                itemParaEditar.IdDiarioObrasImagens,
              );
              if (requestId === previewRequestRef.current) {
                replacePreviewUrl(URL.createObjectURL(blob));
              }
            } catch (e: unknown) {
              pushErrorNotification(
                "Erro",
                processError(e, "Falha ao carregar a imagem!"),
                { isAutoFinish: true },
              );
            }
          }
        } else {
          hookFormImagens.reset();
        }
      } else {
        hookFormImagens.reset();
        setFileUpload(null);
        setLimparArquivo(false);
        replacePreviewUrl(null);
      }
    },
    [
      diarioObrasListImagens,
      hookFormImagens,
      downloadImagemUseCase,
      pushErrorNotification,
      replacePreviewUrl,
    ],
  );

  const handleDeleteDiarioImagensModalOpenState = useCallback(
    (IdDiarioObrasImagens: number | null, openState: boolean) => {
      if (openState) {
        if (IdDiarioObras && diarioObrasListImagens && IdDiarioObrasImagens) {
          setSelectedDiarioObraImagens(
            diarioObrasListImagens.find(
              (x) => x.IdDiarioObrasImagens === IdDiarioObrasImagens,
            ),
          );
        } else {
          setSelectedDiarioObraImagens(undefined);
          setFileUpload(null);
          setFileUploadURL(null);
        }
      }
      setDiarioObraImagensDeleteModal(openState);
    },
    [IdDiarioObras, diarioObrasListImagens],
  );

  const handleDeleteConfirmaDiarioImagens = useCallback(async () => {
    if (selectedDiarioObraImagens) {
      try {
        setSavingImagens(true);
        await deleteImagensUseCase.deleteDiarioObraImagens(
          IdDiarioObras ?? null,
          selectedDiarioObraImagens.IdDiarioObrasImagens,
        );
        await loadDiarioObrasImagensListaState({
          IdDiarioObras: IdDiarioObras,
        });
        pushInfoNotification(
          "Sucesso",
          "Diario de obra Imagens atualizado com sucesso!",
          { isAutoFinish: true },
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"),
          { isAutoFinish: true },
        );
      } finally {
        setSavingImagens(false);
        setDiarioObraImagensDeleteModal(false);
      }
    }
  }, [
    selectedDiarioObraImagens,
    deleteImagensUseCase,
    IdDiarioObras,
    loadDiarioObrasImagensListaState,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleStartFileUpload = useCallback(
    (file: File | null) => {
      previewRequestRef.current++;
      setFileUpload(file);
      if (file) setLimparArquivo(false);
      replacePreviewUrl(file ? URL.createObjectURL(file) : null);
    },
    [replacePreviewUrl],
  );

  const handleDeleteFileUpload = useCallback(() => {
    try {
      const imageInput = document.getElementById("profileImageInput");
      if (imageInput instanceof HTMLInputElement) {
        imageInput.value = "";
      }
    } catch (_: unknown) { }
    previewRequestRef.current++;
    if (selectedDiarioObraImagens?.IdDiarioObrasImagens && selectedDiarioObraImagens.Nome) {
      setLimparArquivo(true);
    }
    setFileUpload(null);
    replacePreviewUrl(null);
  }, [replacePreviewUrl, selectedDiarioObraImagens]);

  const handleDownloadImagem = useCallback(
    async (IdDiarioObrasImagens: number | null) => {
      const item = diarioObrasListImagens.find(
        (x) => x.IdDiarioObrasImagens === IdDiarioObrasImagens,
      );
      if (!item || saving) return;
      try {
        setCustomLoading(true, "Preparando download...");
        await downloadImagemUseCase.downloadDiarioObraImagem(
          item.IdDiarioObrasImagens,
          item.Nome ?? `imagem_${item.IdDiarioObrasImagens}`,
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao fazer download do arquivo!"),
        );
      } finally {
        setCustomLoading(false);
      }
    },
    [
      diarioObrasListImagens,
      saving,
      downloadImagemUseCase,
      setCustomLoading,
      pushErrorNotification,
    ],
  );

  //#endregion

  const handleVoltarDiario = useCallback(() => {
    navigate(`/DiarioObra_lista/`);
  }, [navigate]);

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (IdDiarioObras) {
          changeHistory.handleOpenChangeHistory(
            IdDiarioObras,
            LogTipoTabela.DIARIO_OBRAS,
            true,
          );
        }
      }
    },
    [IdDiarioObras, changeHistory],
  );
  //#endregion

  //#region Paginacao
  const handleTableEquipaChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        paginationEquipamento.setPage(page + 1);
        await loadDiarioObrasEquipamentoListaState({
          IdDiarioObras: IdDiarioObras,
          page: page + 1,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"), { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      IdDiarioObras,
      loadDiarioObrasEquipamentoListaState,
      paginationEquipamento,
      pushErrorNotification,
    ],
  );

  const handleRowsPerTableEquipaChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        paginationEquipamento.setPage(1);
        paginationEquipamento.setRowsPerPage(rowsPerPage);

        await loadDiarioObrasEquipamentoListaState({
          IdDiarioObras: IdDiarioObras,
          page: 1,
          rowsPerPage,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      IdDiarioObras,
      loadDiarioObrasEquipamentoListaState,
      paginationEquipamento,
      pushErrorNotification,
    ],
  );

  const handleTableEfetivoChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        paginationEfetivo.setPage(page + 1);
        await loadDiarioObrasEfetivoListaState({
          IdDiarioObras: IdDiarioObras,
          page: page + 1,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"), { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      IdDiarioObras,
      loadDiarioObrasEfetivoListaState,
      paginationEfetivo,
      pushErrorNotification,
    ],
  );

  const handleRowsPerTableEfetivoChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        paginationEfetivo.setPage(1);
        paginationEfetivo.setRowsPerPage(rowsPerPage);

        await loadDiarioObrasEfetivoListaState({
          IdDiarioObras: IdDiarioObras,
          page: 1,
          rowsPerPage,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"), { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      IdDiarioObras,
      loadDiarioObrasEfetivoListaState,
      paginationEfetivo,
      pushErrorNotification,
    ],
  );

  const handleTableAtividadeChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        paginationAtividades.setPage(page + 1);
        await loadDiarioObrasAtividadesListaState({
          IdDiarioObras: IdDiarioObras,
          page: page + 1,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      IdDiarioObras,
      loadDiarioObrasAtividadesListaState,
      paginationAtividades,
      pushErrorNotification,
    ],
  );

  const handleRowsPerTableAtividadeChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        paginationAtividades.setPage(1);
        paginationAtividades.setRowsPerPage(rowsPerPage);

        await loadDiarioObrasAtividadesListaState({
          IdDiarioObras: IdDiarioObras,
          page: 1,
          rowsPerPage,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      IdDiarioObras,
      loadDiarioObrasAtividadesListaState,
      paginationAtividades,
      pushErrorNotification,
    ],
  );

  const handleTableParalisacoesChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        paginationParalisacoes.setPage(page + 1);
        await loadDiarioObrasParalisacoesListaState({
          IdDiarioObras: IdDiarioObras,
          page: page + 1,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"), { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      IdDiarioObras,
      loadDiarioObrasParalisacoesListaState,
      paginationParalisacoes,
      pushErrorNotification,
    ],
  );

  const handleRowsPerTableParalisacoesChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        paginationParalisacoes.setPage(1);
        paginationParalisacoes.setRowsPerPage(rowsPerPage);

        await loadDiarioObrasParalisacoesListaState({
          IdDiarioObras: IdDiarioObras,
          page: 1,
          rowsPerPage,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      IdDiarioObras,
      loadDiarioObrasParalisacoesListaState,
      paginationParalisacoes,
      pushErrorNotification,
    ],
  );

  const handleTableImagensChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        paginationImagens.setPage(page + 1);
        await loadDiarioObrasImagensListaState({
          IdDiarioObras: IdDiarioObras,
          page: page + 1,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      IdDiarioObras,
      loadDiarioObrasImagensListaState,
      paginationImagens,
      pushErrorNotification,
    ],
  );

  const handleRowsPerTableImagensChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        paginationImagens.setPage(1);
        paginationImagens.setRowsPerPage(rowsPerPage);

        await loadDiarioObrasImagensListaState({
          IdDiarioObras: IdDiarioObras,
          page: 1,
          rowsPerPage,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      IdDiarioObras,
      loadDiarioObrasImagensListaState,
      paginationImagens,
      pushErrorNotification,
    ],
  );

  //#endregion
  return {
    loading,
    loadingEfetivo,
    errorMessage,
    saving,
    pagination,
    paginationEfetivo,
    paginationEquipamento,
    filters,
    filtersEfetivo,
    diarioObrasLista,
    hookForm,
    hookFormEfetivo,
    hookFormImagens,
    hookFormEquipamento,
    hookFormAtividades,
    hookFormParalisacoes,
    IdDiarioObras,
    openEfetivo,
    selectedDiarioObra,
    diarioObrasListaEfetivo,
    diarioObrasListEquipamento,
    totaisEfetivo,
    diarioObraEfetivoDeleteModal,
    selectedDiarioObraEfetivo,
    savingEquipamento,
    diarioObraEquipamentoDeleteModal,
    loadingEquipamento,
    openEquipamento,
    filtersEquipamento,
    openAtividades,
    filtersAtividades,
    filtersImagens,
    paginationAtividades,
    paginationParalisacoes,
    paginationImagens,
    diarioObrasListAtividades,
    loadingAtividades,
    savingAtividades,
    savingParalisacoes,
    savingImagens,
    diarioObraParalisacoesDeleteModal,
    diarioObraAtividadesDeleteModal,
    diarioObraImagensDeleteModal,
    openParalisacoes,
    loadingParalisacoes,
    diarioObrasListParalisacoes,
    fileUploadUrl,
    loadingImagens,
    diarioObrasListImagens,
    openImagens,
    selectedDiarioObraImagens,
    fileUpload,
    read,
    selectedDiarioObraEquipamento,
    selectedDiarioObraAtividades,
    selectedDiarioObraParalisacoes,
    changeHistory,
    funcionarioDropdown,
    linhaSelecionada,
    modalAberto,
    funcionarioTerceirizadoDropdown,
    setModalAberto,
    handleOpenChangeHistory,
    setFileUploadURL,
    setFileUpload,
    setOpenParalisacoes,
    setOpenImagens,
    setOpenEquipamento,
    setOpenAtividades,
    handleReload,
    handleDeleteDiarioEfetivoModalOpenState,
    handleDeleteConfirmaDiarioEfetivo,
    handleDiarioObraSubmit,
    handleEfeivo,
    setOpenEfetivo,
    handleEfetivoSubmit,
    handleEdicaoEfetivoOpenState,
    handleDeleteConfirmaDiarioEquipamento,
    handleDeleteDiarioEquipamentoModalOpenState,
    handleEdicaoEquipamentoOpenState,
    handleEquipamentoSubmit,
    handleEquipamento,
    handleAtividades,
    handleAtividadesSubmit,
    handleEdicaoAtividadesOpenState,
    handleDeleteDiarioAtividadesModalOpenState,
    handleDeleteConfirmaDiarioAtividades,
    handleParalisacoes,
    handleImagens,
    handleParalisacoesSubmit,
    handleEdicaoParalisacoesOpenState,
    handleDeleteDiarioParalisacoesModalOpenState,
    handleDeleteDiarioImagensModalOpenState,
    handleDeleteConfirmaDiarioParalisacoes,
    handleDeleteConfirmaDiarioImagens,
    handleImagensSubmit,
    handleEdicaoImagensOpenState,
    handleStartFileUpload,
    handleDeleteFileUpload,
    handleDownloadImagem,
    handleVoltarDiario,
    handleRowsPerTableImagensChange,
    handleTableImagensChange,
    handleRowsPerTableParalisacoesChange,
    handleTableParalisacoesChange,
    handleRowsPerTableAtividadeChange,
    handleTableAtividadeChange,
    handleRowsPerTableEfetivoChange,
    handleTableEfetivoChange,
    handleRowsPerTableEquipaChange,
    handleTableEquipaChange,
    loadFuncionarioDropDown,
    handleEfetivoFuncionarioOpenState,
    loadFuncionarioTerceirizadoDropDown,
  };
};
